import React from "react";
import { Link } from "react-router-dom";
import { Box, IconButton, Typography } from "@mui/material";
import { motion } from "framer-motion";

import {
    DISCORD_INVITE_URL,
    X_PROFILE_URL,
} from "../../public-site/siteData";
import { SUPPORT_EMAIL } from "./publicFacts";
import {
    DEFAULT_DISCORD_INVITE_URL,
    fetchPublicDiscordInviteUrl,
} from "../../public-site/discordInviteUrl";
import DiscordIcon from "../../public-site/DiscordIcon";
import utils from "../../../universal/utils";
import { colors } from "../dark-hero/colors";
import { PageContainer } from "../dark-hero/PageContainer";

const MotionBox = motion(Box);

const HERO_BG = "#080808";
const FOOTER_MAX_WIDTH = "68rem";
const LOGO_URL =
    "https://ouro-public-assets.s3.eu-west-2.amazonaws.com/init-logo-darkmode.png";
const REVEAL = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.2 },
};

const FOOTER_LINK_SX = {
    display: "block",
    width: "fit-content",
    fontSize: "0.925rem",
    lineHeight: 1.5,
    color: "rgba(255,255,255,0.7)",
    textDecoration: "none",
    transition: "color .25s ease, transform .25s ease",
    "&:hover": {
        color: colors.text.primary,
        transform: "translateX(4px)",
    }
};

const FOOTER_COLUMNS = [
    {
        title: "Pages",
        links: [
            { label: "Home", to: "/" },
            { label: "How it works", to: "/how-it-works" },
            { label: "Match Night", to: "/match-night" },
            { label: "Anti-Cheat", to: "/anti-cheat" },
            { label: "Reputation", to: "/reputation" },
            { label: "Pricing", to: "/pricing" },
            { label: "Trust & Safety", to: "/trust-and-safety" },
        ],
    },
    {
        title: "Platform",
        links: [
            { label: "Privacy requests", href: `mailto:${SUPPORT_EMAIL}` },
            { label: "FAQ", to: "/faq" },
            { label: "About Ouro", to: "/about-us" },
        ],
    },
    {
        title: "Policies",
        links: [
            { label: "Privacy policy", to: "/privacy" },
            { label: "Terms of service", to: "/terms-of-use" },
            { label: "Data protection (GDPR)", to: "/data-protection-policy-and-gdpr" },
        ],
    },
];

function FooterLink({ link }) {
    const sx = FOOTER_LINK_SX;

    if (link.to) {
        return (
            <Typography component={Link} to={link.to} sx={sx}>
                {link.label}
            </Typography>
        );
    }

    return (
        <Typography
            component="a"
            href={link.href}
            target={link.external ? "_blank" : undefined}
            rel={link.external ? "noopener noreferrer" : undefined}
            sx={sx}
        >
            {link.label}
        </Typography>
    );
}

function FooterColumn({ title, links }) {
    return (
        <Box>
            <Typography
                component="h3"
                sx={{
                    fontSize: "0.95rem",
                    fontWeight: 700,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    color: colors.text.primary,
                    mb: 1.5,
                }}
            >
                {title}
            </Typography>
            <Box
                component="nav"
                aria-label={title}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.85,
                    overflow: "visible",
                    pr: 0.75,
                }}
            >
                {links.map((link) => (
                    <FooterLink key={link.label} link={link} />
                ))}
            </Box>
        </Box>
    );
}

const SOCIAL_ICON_SIZE = 24;

function XIcon() {
    return (
        <svg
            width={SOCIAL_ICON_SIZE}
            height={SOCIAL_ICON_SIZE}
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden
        >
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
    );
}

const SOCIAL_ICON_SX = {
    color: "rgba(255,255,255,0.62)",
    width: 44,
    height: 44,
    p: 0,
    backgroundColor: "transparent",
    "& svg": {
        transition: `
        transform 320ms cubic-bezier(.22,1,.36,1),
        filter 320ms cubic-bezier(.22,1,.36,1)
      `,
        transformOrigin: "center",
        willChange: "transform",
        filter: "drop-shadow(0 0 0 rgba(232,163,61,0))",
    },
    transition: "color 320ms cubic-bezier(.22,1,.36,1)",
    "&:hover": {
        color: colors.text.primary,
        "& svg": {
            transform: "scale(1.1)",
            filter: "drop-shadow(0 0 8px rgba(232,163,61,0.15))",
        },
    },
};

export default function Footer() {
    const [discordUrl, setDiscordUrl] = React.useState(DEFAULT_DISCORD_INVITE_URL);

    React.useEffect(() => {
        let cancelled = false;

        fetchPublicDiscordInviteUrl(utils.getEdgeBasePath())
            .then((url) => {
                if (!cancelled) setDiscordUrl(url);
            })
            .catch(() => { });

        return () => {
            cancelled = true;
        };
    }, []);

    const columns = React.useMemo(
        () =>
            FOOTER_COLUMNS.map((column) => {
                if (column.title !== "Platform") return column;

                return {
                    ...column,
                    links: column.links.map((link) =>
                        link.label === "Discord" ? { ...link, href: discordUrl } : link
                    ),
                };
            }),
        [discordUrl]
    );

    const socialLinks = [
        { label: "Discord", href: discordUrl, icon: <DiscordIcon size={SOCIAL_ICON_SIZE} /> },
        { label: "X (Twitter)", href: X_PROFILE_URL, icon: <XIcon /> },
    ];

    return (
        <Box
            component="footer"
            sx={{
                position: "relative",
                width: "100%",
                backgroundColor: HERO_BG,
                overflow: "visible",
            }}
        >
            <Box
                sx={{
                    width: 80,
                    height: 80,
                    borderRadius: "50%",
                    mx: "auto",
                    mb: "-40px",
                    background: `
                    radial-gradient(
                        circle,
                        rgba(232,163,61,0.18),
                        transparent 75%
                    )
                    `,
                    filter: "blur(40px)",
                    pointerEvents: "none",
                }}
            />
            <Box
                sx={{
                    width: "100%",
                    mx: "auto",
                    position: "relative",
                    mb: { xs: 6, md: 8 },
                }}
            >
                <Box
                    sx={{
                        height: "1px",
                        background: `
                            linear-gradient(
                            90deg,
                            transparent,
                            rgba(255,255,255,0.06) 20%,
                            rgba(232,163,61,0.25) 50%,
                            rgba(255,255,255,0.06) 80%,
                            transparent
                            )
                        `,
                    }}
                />
            </Box>
            <PageContainer
                sx={{
                    maxWidth: FOOTER_MAX_WIDTH,
                    width: "100%",
                    mx: "auto",
                    px: { xs: 2, md: 0 },
                    overflowX: "visible",
                }}
            >
                <MotionBox
                    {...REVEAL}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    sx={{
                        pt: { xs: "4rem", md: "5rem" },
                        pb: { xs: "2rem", md: "2.5rem" },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            alignItems: { xs: "flex-start", md: "flex-start" },
                            justifyContent: { md: "space-between" },
                            gap: { xs: 4, md: 3 },
                        }}
                    >
                        <Box
                            sx={{
                                flex: "0 0 auto",
                                maxWidth: "18rem",
                                pr: { md: "4rem" },
                            }}
                        >
                            <Box
                                component={Link}
                                to="/"
                                aria-label="Ouro home"
                                sx={{
                                    display: "inline-flex",
                                    mb: 2.5,
                                    textDecoration: "none",
                                }}
                            >
                                <Box
                                    component="img"
                                    src={LOGO_URL}
                                    alt="Ouro"
                                    sx={{ height: 40, width: "auto", display: "block" }}
                                />
                            </Box>

                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.35 }}>
                                {[
                                    "Ouro Matchmaking",
                                    "is building a more accountable Counter-Strike experience around verified players, reputation-aware trust, and stronger matchmaking controls.",
                                ].map((line) => (
                                    <Typography
                                        key={line}
                                        sx={{
                                            fontSize: "0.85rem",
                                            lineHeight: 1.55,
                                            color: colors.text.secondary,
                                        }}
                                    >
                                        {line}
                                    </Typography>
                                ))}
                            </Box>
                            <Typography
                                sx={{
                                    mt: 2,
                                    fontSize: "0.9rem",
                                    lineHeight: 1.5,
                                    color: colors.text.secondary,
                                }}
                            >
                                Support:{" "}
                                <Typography
                                    component={Link}
                                    to="/support"
                                    sx={{
                                        position: "relative",
                                        color: colors.text.primary,
                                        fontWeight: 600,
                                        textDecoration: "none",
                                        fontSize: "inherit",
                                        display: "inline",
                                        "&::after": {
                                            content: '""',
                                            position: "absolute",
                                            left: 0,
                                            bottom: "-2px",
                                            width: 0,
                                            height: "1px",
                                            background: colors.text.primary,
                                            transition: "width 0.25s ease",
                                        },
                                        "&:hover::after": {
                                            width: "100%",
                                        },
                                    }}
                                >
                                    Open a ticket
                                </Typography>
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: { xs: "wrap", md: "nowrap" },
                                alignItems: "flex-start",
                                gap: "3.75rem",
                                flex: { md: "0 1 auto" },
                                overflow: "visible",
                            }}
                        >
                            {columns.map((column) => (
                                <Box
                                    key={column.title}
                                    sx={{ flex: "0 0 auto", overflow: "visible" }}
                                >
                                    <FooterColumn title={column.title} links={column.links} />
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            mt: { xs: 4, md: 4 },
                            pt: { xs: 2.5, md: 3 },
                            borderTop: `0.5px solid ${colors.border.default}`,
                            display: "flex",
                            flexDirection: { xs: "column-reverse", sm: "row" },
                            alignItems: { xs: "center", sm: "center" },
                            justifyContent: { xs: "center", sm: "space-between" },
                            gap: 2,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: { xs: "0.85rem", md: "0.95rem" },
                                letterSpacing: "0.03em",
                                color: colors.text.muted,
                                textAlign: { xs: "center", sm: "left" },
                                width: { xs: "100%", sm: "auto" },
                            }}
                        >
                            © {new Date().getFullYear()} Ouro Matchmaking. All rights reserved.
                        </Typography>

                        <Box
                            component="nav"
                            aria-label="Social and contact"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: { xs: "center", sm: "flex-end" },
                                gap: 0.5,
                                width: { xs: "100%", sm: "auto" },
                            }}
                        >
                            {socialLinks.map(({ label, href, icon }) => (
                                <IconButton
                                    key={label}
                                    component="a"
                                    href={href}
                                    target={href.startsWith("http") ? "_blank" : undefined}
                                    rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                                    aria-label={label}
                                    disableRipple
                                    sx={SOCIAL_ICON_SX}
                                >
                                    {icon}
                                </IconButton>
                            ))}
                        </Box>
                    </Box>
                </MotionBox>
            </PageContainer>
        </Box>
    );
}
